// Lightweight anonymous funnel tracker.
// Persists a session id per browser tab so we can stitch events together
// across the landing → demo → register flow without identifying the user.
// Once a user signs up we still emit events with their user_id so later
// in-app milestones (signature, install, connection, ad) can be attributed
// to the same funnel session.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ENDPOINT = BACKEND_URL ? `${BACKEND_URL}/api/analytics/funnel-event` : null;
const SID_KEY = 'cm_funnel_sid';

export const getFunnelSessionId = () => {
  try {
    let sid = sessionStorage.getItem(SID_KEY);
    if (!sid) {
      sid = `f_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(SID_KEY, sid);
    }
    return sid;
  } catch {
    return `f_${Math.random().toString(36).slice(2, 12)}`;
  }
};

const getUserId = () => {
  try {
    const userJson = localStorage.getItem('user');
    if (userJson) {
      const u = JSON.parse(userJson);
      return u?.id || null;
    }
  } catch { /* ignore */ }
  return null;
};

export const logFunnel = (event, metadata = {}) => {
  if (!ENDPOINT || !event) return;
  const payload = {
    event,
    session_id: getFunnelSessionId(),
    user_id: getUserId(),
    metadata,
  };
  try {
    const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
    if (navigator.sendBeacon && navigator.sendBeacon(ENDPOINT, blob)) return;
  } catch { /* fall through */ }
  fetch(ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch(() => {});
};

// Helper to track time-on-page. Returns a cleanup function.
// Fires `<dwellEvent>` with metadata.ms when the user leaves
// (pagehide / visibilitychange hidden / unmount). De-duped so it
// only emits once even if multiple lifecycle hooks fire.
export const trackDwellTime = (dwellEvent) => {
  const startedAt = Date.now();
  let sent = false;
  const flush = () => {
    if (sent) return;
    sent = true;
    const ms = Date.now() - startedAt;
    logFunnel(dwellEvent, { ms });
  };
  const onVisibility = () => { if (document.visibilityState === 'hidden') flush(); };
  window.addEventListener('pagehide', flush);
  document.addEventListener('visibilitychange', onVisibility);
  return () => {
    window.removeEventListener('pagehide', flush);
    document.removeEventListener('visibilitychange', onVisibility);
    flush();
  };
};
